import { InsertShipment, ShipmentDiscount } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { useState } from "react";

interface CostBreakdownProps {
  shipment: InsertShipment;
  isCostBreakdownOpen?: boolean;
  setIsCostBreakdownOpen?: (o: boolean) => void;
  shipmentDiscounts: Partial<ShipmentDiscount>[];
}

export default function CostBreakdown({
  shipment,
  isCostBreakdownOpen = false,
  setIsCostBreakdownOpen,
  shipmentDiscounts,
}: CostBreakdownProps) {
  const [isOpen, setIsOpen] = useState(isCostBreakdownOpen);

  const toggle = () => {
    setIsOpen((o) => !o);
    setIsCostBreakdownOpen?.(!isOpen);
  };

  return (
    <div className="space-y-2">
      <span className="underline cursor-pointer" onClick={toggle}>
        {isOpen ? "Hide" : "Show"} cost breakdown
      </span>

      <div
        className={cn(
          "overflow-hidden transition-all duration-300 ease-[cubic-bezier(.4,0,.2,1)]",
          isOpen
            ? "max-h-[1000px] opacity-100 translate-y-0"
            : "max-h-0 opacity-0 -translate-y-2 pointer-events-none"
        )}
      >
        <div className="flex flex-col max-w-md pt-2">
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Base Freight Cost</span>
            <span>${shipment.base_freight_cost?.toFixed(2)}</span>
          </div>

          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">
              Sub Total (+$
              {(
                (shipment?.pickup_fee || 0) +
                (shipment?.delivery_fee || 0) +
                (shipment?.extra_charge || 0) +
                ((shipment.is_express && shipment?.express_fee) || 0)
              ).toFixed(2)}
              )
            </span>

            <span
              className={cn(
                !shipment.customs_tax_amount && !shipment.discount_amount
                  ? "text-xl font-bold text-[#254164]"
                  : "font-bold"
              )}
            >
              ${shipment.sub_total?.toFixed(2)}
            </span>
          </div>

          {!!shipment.is_express && (
            <div className="flex justify-between gap-4 pl-4">
              <span className="text-muted-foreground">+ Express Fee</span>

              <span>${shipment.express_fee?.toFixed(2) || 0}</span>
            </div>
          )}

          {!!shipment.is_pickup_requested && (
            <div className="flex justify-between gap-4 pl-4">
              <span className="text-muted-foreground">+ Pick Up Fee</span>

              <span>${shipment.pickup_fee?.toFixed(2) || 0}</span>
            </div>
          )}

          {!!shipment.is_delivery_requested && (
            <div className="flex justify-between gap-4 pl-4">
              <span className="text-muted-foreground">+ Delivery Fee</span>

              <span>${shipment.delivery_fee?.toFixed(2) || 0}</span>
            </div>
          )}

          {!!shipment.extra_charge && (
            <div className="flex justify-between gap-4 pl-4">
              <span className="text-muted-foreground">+ Extra Charge</span>

              <span>${shipment.extra_charge?.toFixed(2)}</span>
            </div>
          )}

          {!!shipment.discount_amount && (
            <>
              <div className="flex justify-between gap-4">
                <span className="text-green-500">
                  Discounts & Promos (-$
                  {shipment.discount_amount?.toFixed(2)})
                </span>

                {/* totalCost = adjustedSubTotal + customsTaxAmount, so... */}
                <span
                  className={cn(
                    !!!shipment.customs_tax_amount
                      ? "text-xl font-bold text-[#254164]"
                      : "font-bold"
                  )}
                >
                  $
                  {(
                    shipment.total_cost - (shipment?.customs_tax_amount || 0)
                  )?.toFixed(2)}
                </span>
              </div>

              {!!shipmentDiscounts.length &&
                shipmentDiscounts.map((discount) => {
                  const discountMode = !!discount.promo_id
                    ? "promo"
                    : "discount";
                  const discountCode =
                    discountMode === "promo"
                      ? discount.promo?.code
                      : discount.discount?.code;
                  const discountAmount =
                    discountMode === "promo"
                      ? discount.promo?.amount
                      : discount.discount?.amount;
                  const discountType =
                    discountMode === "promo"
                      ? discount.promo?.promo_type
                      : discount.discount?.discount_type;

                  return (
                    <div className="flex justify-between gap-4 pl-4">
                      <span className="text-green-500">- {discountCode}</span>

                      <span className="text-green-500">
                        -
                        {discountType === "fixed"
                          ? `$${discountAmount}`
                          : `${discountAmount}%`}
                      </span>
                    </div>
                  );
                })}
            </>
          )}

          {!!shipment.customs_tax_amount && (
            <div className="flex justify-between gap-4">
              <span className="text-destructive">
                Customs Tax (
                {shipment.customs_tax_type === "percentage"
                  ? `+${shipment.customs_tax_rate}%`
                  : `+$${shipment.customs_tax_rate}`}
                )
              </span>

              <span className="text-xl font-bold text-[#254164]">
                {" $"}
                {shipment.total_cost}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
